import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  getMonthlyLeaveSummary,
  getLeaveTransactions,
} from "../../api/api";

// =====================================================
// MAIN
// =====================================================

export default function EmployeeMonthlySummary({

  userId,

  userName,

}) {

  const [monthlySummary, setMonthlySummary] =
    useState([]);

  const [transactions, setTransactions] =
    useState([]);

  const [selectedMonth, setSelectedMonth] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [year, setYear] =
    useState(
      new Date().getFullYear()
    );

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {

    if (userId) {

      fetchMonthlySummary();
    }

  }, [userId, year]);

  // =====================================================
  // FETCH MONTHLY SUMMARY
  // =====================================================

  const fetchMonthlySummary =
    async () => {

      try {

        setLoading(true);

        const summary =
          await getMonthlyLeaveSummary(
            userId,
            year
          );

        const txns =
          await getLeaveTransactions(
            userId
          );

        setMonthlySummary(
          summary || []
        );

        setTransactions(
          txns || []
        );

      } catch (error) {

        console.error(error);

      } finally {

        setLoading(false);
      }
    };

  // =====================================================
  // FILTER TRANSACTIONS BY MONTH
  // =====================================================

  const filteredTransactions =
    useMemo(() => {

      if (!selectedMonth) {

        return [];
      }

      return transactions.filter(
        (transaction) => {

          const month =
            new Date(
              transaction.created_at
            ).toLocaleString(
              "default",
              {
                month: "long",
                year: "numeric",
              }
            );

          return (
            month === selectedMonth
          );
        }
      );

    }, [
      transactions,
      selectedMonth,
    ]);

  // =====================================================
  // BADGE COLOR
  // =====================================================

  const getBadgeColor =
    (type) => {

      if (
        type?.includes("rollback")
      ) {

        return {
          background: "#dbeafe",
          color: "#1d4ed8",
        };
      }

      if (
        type?.includes("loss")
      ) {

        return {
          background: "#ffedd5",
          color: "#ea580c",
        };
      }

      if (
        type?.includes("deducted")
      ) {

        return {
          background: "#fee2e2",
          color: "#dc2626",
        };
      }

      return {
        background: "#dcfce7",
        color: "#16a34a",
      };
    };

  // =====================================================
  // UI
  // =====================================================

  return (

    <div style={styles.page}>

      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div style={styles.header}>

        <div>

          <div style={styles.title}>
            Employee Monthly Summary
          </div>

          <div style={styles.subtitle}>
            {userName}
          </div>

        </div>

        <select
          value={year}

          onChange={(e) =>
            setYear(
              e.target.value
            )
          }

          style={styles.yearSelect}
        >

          <option value="2026">
            2026
          </option>

          <option value="2025">
            2025
          </option>

          <option value="2024">
            2024
          </option>

        </select>

      </div>

      {/* ================================================= */}
      {/* MONTHLY SUMMARY */}
      {/* ================================================= */}

      <div style={styles.card}>

        <div style={styles.sectionTitle}>
          Monthly Analytics
        </div>

        {loading ? (

          <div style={styles.loading}>
            Loading summary...
          </div>

        ) : (

          <div style={styles.tableWrapper}>

            <table style={styles.table}>

              <thead>

                <tr>

                  <th style={styles.th}>
                    Month
                  </th>

                  <th style={styles.th}>
                    Planned Used
                  </th>

                  <th style={styles.th}>
                    Unplanned Used
                  </th>

                  <th style={styles.th}>
                    Credits
                  </th>

                  <th style={styles.th}>
                    Loss Of Pay
                  </th>

                  <th style={styles.th}>
                    Action
                  </th>

                </tr>

              </thead>

              <tbody>

                {monthlySummary.map((month) => (

                  <tr
                    key={month.month}
                  >

                    <td style={styles.td}>
                      {month.month}
                    </td>

                    <td style={styles.td}>
                      {month.planned_used}
                    </td>

                    <td style={styles.td}>
                      {month.unplanned_used}
                    </td>

                    <td style={styles.td}>
                      {month.credits}
                    </td>

                    <td style={styles.td}>
                      {month.loss_of_pay}
                    </td>

                    <td style={styles.td}>

                      <button
                        style={styles.viewBtn}

                        onClick={() =>
                          setSelectedMonth(
                            month.month
                          )
                        }
                      >
                        View Transactions
                      </button>

                    </td>

                  </tr>
                ))}

              </tbody>

            </table>

          </div>
        )}

      </div>

      {/* ================================================= */}
      {/* TRANSACTION HISTORY */}
      {/* ================================================= */}

      {selectedMonth && (

        <div style={styles.card}>

          <div style={styles.sectionTitle}>

            {selectedMonth}
            {" "}
            Transactions

          </div>

          <div style={styles.tableWrapper}>

            <table style={styles.table}>

              <thead>

                <tr>

                  <th style={styles.th}>
                    Date
                  </th>

                  <th style={styles.th}>
                    Leave Type
                  </th>

                  <th style={styles.th}>
                    Transaction
                  </th>

                  <th style={styles.th}>
                    Days
                  </th>

                  <th style={styles.th}>
                    Before
                  </th>

                  <th style={styles.th}>
                    After
                  </th>

                  <th style={styles.th}>
                    Remarks
                  </th>

                </tr>

              </thead>

              <tbody>

                {filteredTransactions.map((transaction) => (

                  <tr
                    key={transaction.id}
                  >

                    <td style={styles.td}>

                      {
                        new Date(
                          transaction.created_at
                        ).toLocaleDateString()
                      }

                    </td>

                    <td style={styles.td}>
                      {transaction.leave_type_name}
                    </td>

                    <td style={styles.td}>

                      <span
                        style={{
                          ...styles.badge,
                          ...getBadgeColor(
                            transaction.transaction_type
                          ),
                        }}
                      >

                        {
                          transaction.transaction_label
                        }

                      </span>

                    </td>

                    <td style={styles.td}>
                      {transaction.days}
                    </td>

                    <td style={styles.td}>
                      {
                        transaction.balance_before
                      }
                    </td>

                    <td style={styles.td}>
                      {
                        transaction.balance_after
                      }
                    </td>

                    <td style={styles.td}>
                      {transaction.remarks}
                    </td>

                  </tr>
                ))}

              </tbody>

            </table>

          </div>

        </div>
      )}

    </div>
  );
}

// =====================================================
// STYLES
// =====================================================

const styles = {

  page: {

    padding: "24px",

    background: "#f8fafc",

    minHeight: "100vh",
  },

  header: {

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    marginBottom: "24px",
  },

  title: {

    fontSize: "32px",

    fontWeight: "800",

    color: "#111827",
  },

  subtitle: {

    marginTop: "6px",

    color: "#6b7280",
  },

  yearSelect: {

    padding: "12px 16px",

    borderRadius: "12px",

    border: "1px solid #d1d5db",

    background: "#fff",
  },

  card: {

    background: "#fff",

    borderRadius: "18px",

    padding: "24px",

    border: "1px solid #e5e7eb",

    marginBottom: "24px",
  },

  sectionTitle: {

    fontSize: "22px",

    fontWeight: "700",

    marginBottom: "20px",
  },

  loading: {

    textAlign: "center",

    padding: "40px",

    color: "#6b7280",
  },

  tableWrapper: {

    overflowX: "auto",
  },

  table: {

    width: "100%",

    borderCollapse: "collapse",
  },

  th: {

    textAlign: "left",

    padding: "16px",

    background: "#f9fafb",

    borderBottom: "1px solid #e5e7eb",

    fontWeight: "700",
  },

  td: {

    padding: "16px",

    borderBottom: "1px solid #f1f5f9",
  },

  badge: {

    padding: "6px 12px",

    borderRadius: "999px",

    fontSize: "12px",

    fontWeight: "700",
  },

  viewBtn: {

    padding: "8px 14px",

    borderRadius: "10px",

    border: "none",

    background: "#2563eb",

    color: "#fff",

    cursor: "pointer",

    fontWeight: "600",
  },
};